import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import ShadcnUserTable from '../components/ShadcnUserTable';
import { getUserData } from '../services/api';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/Button';
import { Search } from 'lucide-react';

/**
 * Users page component
 * @returns {JSX.Element} UsersPage component
 */
const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('userId'); // Default search by userId

  const [pagination, setPagination] = useState({
    page: 0,
    limit: 50, // Show 50 items per page
    total: 0,
    totalPages: 0
  });

  // Add caching to prevent duplicate API calls
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [lastFetchParams, setLastFetchParams] = useState(null);

  // Function to transform API user data to match the table structure
  const transformUserData = (apiUsers) => {
    return apiUsers.map(user => ({
      id: user.userid,
      firstName: '', // Not needed as we'll use username
      lastName: '', // Not needed as we'll use username
      username: user.username,
      email: user.emails && user.emails.length > 0 ? user.emails.join(', ') : 'N/A',
      phone: user.mobilenumber || 'N/A',
      status: 'active', // Static value for now
      lastLogin: new Date().toISOString(), // Static value for now
      emails: user.emails || []
    }));
  };

  const fetchUsers = useCallback(async (page = 0) => {
    try {
      setLoading(true);

      // Prepare search parameters
      const params = { page, limit: pagination.limit };

      // Add search filters based on the selected search type
      if (searchTerm && searchTerm.trim() !== '') {
        if (searchType === 'userId') {
          // Only add userId if it's a valid number
          if (!isNaN(searchTerm.trim())) {
            params.userId = parseInt(searchTerm.trim());
          }
        } else if (searchType === 'email') {
          params.email = searchTerm.trim();
        }
      }

      // Check if we're making the same request as before
      const currentParams = JSON.stringify(params);
      if (hasInitiallyLoaded && lastFetchParams === currentParams) {
        console.log('Skipping duplicate API call for users');
        setLoading(false);
        return;
      }

      console.log('Fetching users with params:', params);

      const data = await getUserData(params);

      console.log('API Response:', data);

      // Transform the API data to match our table structure
      const transformedUsers = transformUserData(data.users);
      console.log(`Showing ${transformedUsers.length} users out of ${data.pagination.total} total`);
      setUsers(transformedUsers);

      // Update pagination
      const paginationData = {
        page: data.pagination.page,
        limit: data.pagination.limit,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages
      };

      console.log('Pagination Data:', paginationData);

      setPagination(paginationData);
      setLastFetchParams(currentParams);
      setHasInitiallyLoaded(true);

      setError(null);
    } catch (err) {
      setError('Failed to load users. Please try again later.');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, searchType, pagination.limit, hasInitiallyLoaded, lastFetchParams]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle search button click
  const handleSearch = () => {
    fetchUsers(0); // Reset to first page when searching
  };

  const handleUserClick = (user) => {
    console.log('User clicked:', user);
    // Navigate to the user details page with the user ID
    navigate(`/userInfo/${user.id}`);
  };

  return (
    <MainLayout activeTab="users">
      <div className="flex flex-col h-screen">
        {/* Search controls */}
        <div className="flex-shrink-0 px-6 py-3 bg-gray-50 border-b">
          <div className="flex gap-2 max-w-lg">
            <Select value={searchType} onValueChange={(value) => {
              setSearchType(value);
              setSearchTerm(''); // Clear search term when changing search type
            }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Search by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="userId">User ID</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-grow">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={searchType === 'userId' ? "Search by User ID..." : "Search by Email..."}
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>

            <Button onClick={handleSearch} disabled={loading}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </div>

        {/* Users table */}
        <div className="flex-1 bg-white min-h-0 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center flex-1">
              <p className="text-gray-600">Loading users...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center flex-1">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center flex-1">
              <p className="text-gray-600">No users found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className="flex-1 min-h-0">
                <ShadcnUserTable users={users} onUserClick={handleUserClick} />
              </div>

              {/* Pagination */}
              {pagination.total > pagination.limit && (
                <div className="flex-shrink-0 px-6 py-2 border-t border-gray-200 bg-gray-50">
                  {/* Mobile pagination controls */}
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => fetchUsers(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        pagination.page === 0
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => fetchUsers(Math.min(pagination.totalPages - 1, pagination.page + 1))}
                      disabled={pagination.page >= pagination.totalPages - 1}
                      className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                        pagination.page >= pagination.totalPages - 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>

                  {/* Desktop pagination controls */}
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{users.length > 0 ? pagination.page * pagination.limit + 1 : 0}</span> to{' '}
                        <span className="font-medium">
                          {Math.min((pagination.page + 1) * pagination.limit, pagination.total)}
                        </span>{' '}
                        of <span className="font-medium">{pagination.total}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        {/* Previous page button */}
                        <button
                          onClick={() => fetchUsers(Math.max(0, pagination.page - 1))}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>

                        {/* First page */}
                        <button
                          onClick={() => fetchUsers(0)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pagination.page === 0
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          1
                        </button>

                        {/* Show more pages if totalPages > 1 */}
                        {pagination.totalPages > 1 && (
                          <button
                            onClick={() => fetchUsers(1)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pagination.page === 1
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            2
                          </button>
                        )}

                        {/* Show more pages if totalPages > 2 */}
                        {pagination.totalPages > 2 && (
                          <button
                            onClick={() => fetchUsers(2)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pagination.page === 2
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            3
                          </button>
                        )}

                        {/* Next page button */}
                        <button
                          onClick={() => fetchUsers(Math.min(pagination.totalPages - 1, pagination.page + 1))}
                          disabled={pagination.page >= pagination.totalPages - 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                            pagination.page >= pagination.totalPages - 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default UsersPage;
